<template>
  <div class="personalize">
    <div class="personalize__image">
      <label class="personalize__label">Bild</label>
      <croppa
        v-model="croppa"
        :initial-image="image"
        :accept="'image/png,image/jpeg'"
        :placeholder="'Bild hochladen'"
        :placeholder-font-size="18"
        :file-size-limit="2000048000"
        :replace-drop="true"
        :prevent-white-space="true"
        :canvas-color="'#ffffff'"
        :show-remove-button="false"
        :zoom-speed="15"
        :width="this.imageWidth"
        :height="this.imageHeight"
        :show-loading="false"
        :quality="croppaQuality"
        :input-attrs="{
          class: 'personalize__upload',
        }"
        @image-loading="imageLoading = true"
        @new-image="onImageSelected"
        @image-remove="imageChanged = true"
        @move="imageChanged = true"
        @zoom="imageChanged = true"
        @initial-image-loaded="onInitialImageLoaded()"
      >
        <span :class="spinnerClasses()">
          <IconSpinner />
        </span>
      </croppa>
      <div :class="imageActionsClasses()">
        <a
          class="personalize__image-action personalize__image-action--delete"
          @click="removeImage()"
        >
          <IconTrash />
        </a>
        <a
          class="personalize__image-action personalize__image-action--rotate"
          @mousedown="rotateImage()"
        >
          <IconRotate />
        </a>

        <a
          class="personalize__image-action personalize__image-action--zoom-out"
          @mousedown="imageZoomOut()"
        >
          <IconSearchMinus />
        </a>
        <a
          class="personalize__image-action personalize__image-action--zoom-in"
          @mousedown="imageZoomIn()"
        >
          <IconSearchPlus />
        </a>
      </div>
      <div class="personalize__image-title">
        {{ $store.getters.nickName }}
      </div>
    </div>
    <div class="personalize__description">
      <label class="personalize__label">{{
        str('settings.publicCubeDescriptionLabel')
      }}</label>
      <textarea
        v-model="cubeDescription"
        class="personalize__description-textarea"
        @input="descriptionChanged = true"
      ></textarea>

      <LinkButton
        v-if="showSaveButton"
        icon-right="Arrow"
        form="cubePersonalization"
        :text="str('global.save')"
        @click.native="saveChanges()"
        :extra-classes="['personalize__save']"
      />
    </div>
  </div>
</template>

<script>
  import IconTrash from '../icons/Trash'
  import IconSearchPlus from '../icons/SearchPlus'
  import IconSearchMinus from '../icons/SearchMinus'
  import IconRotate from '../icons/Rotate'
  import IconSpinner from '../icons/Spinner'
  import LinkButton from '@/components/LinkButton'

  export default {
    name: 'personalize',
    components: {
      IconTrash,
      IconSearchPlus,
      IconSearchMinus,
      IconRotate,
      IconSpinner,
      LinkButton,
    },
    data() {
      return {
        imageChanged: false,
        descriptionChanged: false,
        croppa: {},
        // This is needed due to the way croppa handles/calculates the scale value
        // for its metadata.
        // See https://github.com/zhanziyang/vue-croppa/issues/49#issuecomment-345588742
        // We need to divide the returned scale value by the quality value to get
        // a useful scale value. Otherwise we get a scale value of 2 without any
        // scaling going on and it's unclear why, because croppa defaults to a
        // quality of 2.
        croppaQuality: 2,
        imageSelected: false,
        hideImageActionsDelay: 200,
        zoomCount: 0,
        // How often croppa's zoom method is being called for every time a user
        // clicks on zoom in/out.
        // Note that croppa itself does not provide animation for zooming. This
        // is why we're using requestAnimationFrame and call the zoom method
        // several times to get a halfway decent animation.
        zoomAnimationSteps: 5,
        cubeDescription: false,
        imageWidth: 200,
        imageHeight: 250,
        imageLoading: false,
      }
    },
    beforeMount() {
      this.cubeDescription = this.$store.getters.cubeDescription
    },
    mounted() {
      if (this.image) {
        this.imageSelected = this.image
        this.imageLoading = true
      }
    },
    computed: {
      image() {
        return this.$store.getters.originalImage
      },
      showSaveButton() {
        return this.imageChanged || this.descriptionChanged
      },
    },
    methods: {
      onImageSelected() {
        this.imageLoading = false
        this.imageSelected = true
        this.imageChanged = true
      },
      imageActionsClasses() {
        let classes = ['personalize__image-actions']
        if (this.imageSelected) {
          classes.push('personalize__image-actions--visible')
        }

        return classes.join(' ')
      },
      spinnerClasses() {
        let classes = ['personalize__image-loading']
        if (this.imageLoading) {
          classes.push('personalize__image-loading--visible')
        }
        return classes
      },
      imageZoomIn() {
        this.zoomCount = 0
        window.requestAnimationFrame(this.imageZoomInRaw)
      },
      imageZoomInRaw() {
        this.croppa.zoom(true)
        this.zoomCount++

        if (this.zoomCount < this.zoomAnimationSteps) {
          window.requestAnimationFrame(this.imageZoomInRaw)
        } else {
          this.zoomCount = 0
        }
      },
      imageZoomOut() {
        this.zoomCount = 0
        window.requestAnimationFrame(this.imageZoomOutRaw)
      },
      imageZoomOutRaw() {
        this.croppa.zoom(false)
        this.zoomCount++

        if (this.zoomCount < this.zoomAnimationSteps) {
          window.requestAnimationFrame(this.imageZoomOutRaw)
        } else {
          this.zoomCount = 0
        }
      },
      removeImage() {
        this.croppa.remove()
        // Hiding the actions immediately feels weird. Let's wait a little.
        setTimeout(() => {
          this.imageSelected = false
        }, this.hideImageActionsDelay)
      },
      rotateImage() {
        this.croppa.rotate(-1)
      },
      async saveChanges() {
        try {
          this.$store.commit('setFormState', {
            form: 'cubePersonalization',
            state: 'submitting',
          })
          await this.updateCubeData()
          this.$store.commit('setFormState', {
            form: 'cubePersonalization',
            state: false,
          })

          this.imageChanged = false
          this.descriptionChanged = false
        } catch (e) {
          return false
        }
      },
      updateCubeData() {
        if (!this.imageChanged && !this.descriptionChanged) {
          return
        }

        let data = {}

        if (this.descriptionChanged) {
          data.description = this.cubeDescription
        }

        if (this.imageChanged) {
          const metadata = this.croppa.getMetadata()
          let rotation = 0
          switch (metadata.orientation) {
            case 1:
              rotation = 0
              break

            case 8:
              rotation = 90
              break

            case 3:
              rotation = 180
              break

            case 6:
              rotation = 270
              break
          }

          data.crop = {
            width: this.imageWidth,
            height: this.imageHeight,
            scale: metadata.scale / this.croppaQuality,
            rotation: rotation,
            x: (metadata.startX / this.croppaQuality) * -1,
            y: (metadata.startY / this.croppaQuality) * -1,
          }
          data.image = this.croppa.$refs.fileInput.files[0]
        }

        return this.$store.dispatch('updateCubeData', data)
      },
      onInitialImageLoaded() {
        this.croppa.applyMetadata(this.$store.getters.imageCropData)
        this.imageLoading = false
      },
    },
  }
</script>

<style lang="scss">
  $image-actions-width: spacer(3);
  $image-actions-margin-left: spacer(0.5);

  .personalize {
    margin-bottom: spacer(3);
    // When an images is already set and we add it to croppa using a slot it
    // renders the slot/image in html and just hides it badly which causes a
    // vertical scrollbar to be shown. Let's hide it.
    overflow: hidden;

    @media (min-width: $bp-sm) {
      display: flex;
    }

    .sk-fading-circle {
      z-index: 9999;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      //width: 40px;
      //height: 40px;
      //border: 5px solid red;
    }

    &__label {
      display: block;
      width: 100%;
    }

    &__image {
      display: flex;
      flex-wrap: wrap;
      flex-shrink: 2;

      canvas {
        border: 3px dashed rgba(0, 0, 0, 0.1);
        border-radius: $border-radius;
        cursor: pointer;
        // Needed to prevent unwanted vertical whitespace.
        vertical-align: middle;
      }

      .croppa {
        &-container {
          position: relative;
          //background: $white;
          // Allow our image actions to be hidden behind the croppa container.
          z-index: 1;
        }

        &--has-target {
          canvas {
            border: none;
            cursor: move;
          }
        }
      }

      // The spinner.
      &-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: spacer(5);
        height: spacer(5);
        visibility: hidden;
        opacity: 0;
        transition: opacity 0.3s ease 0s, visibility 0s linear 0.5s;

        &--visible {
          visibility: visible;
          opacity: 1;
          animation: spin-centered 1.8s linear 0s infinite;
          transition: visibility 0s linear 0s, opacity 0.3s ease 0.1s;
        }
      }

      &-actions {
        display: flex;
        flex-direction: column;
        margin-left: $image-actions-margin-left;
        justify-content: flex-end;
        width: $image-actions-width;
        transform: translateX(-150%);
        transition: $transition-base;

        &--visible {
          transform: translateX(0);
        }
      }

      &-action {
        cursor: pointer;
        display: inline-block;
        padding: 0.25em;
        color: $grey-dark;
        transition: $transition-base;
        text-align: center;

        &:hover,
        &:focus,
        &:active {
          color: $grey-darker;
        }

        &:last-child {
          margin-top: spacer(0.25);
        }

        .icon {
          max-width: 100%;
          vertical-align: middle;
        }

        &--delete {
        }
        &--rotate {
          margin-top: spacer(0.25);
          margin-bottom: auto;
        }
      }

      &-title {
        // Must equal imageWidth to align the title properly.
        width: 200px;
        margin-right: $image-actions-width + $image-actions-margin-left;
        text-align: center;
        font-weight: bold;
        margin-top: spacer(0.75);
        font-size: $font-size-md;
      }
    }

    &__description {
      flex-grow: 1;
      width: 100%;
      margin-top: spacer(2);

      @media (min-width: $bp-sm) {
        margin-left: spacer(2);
        margin-top: 0;
      }

      &-textarea {
        min-height: 7em;
      }
    }

    &__save {
      margin-top: spacer(1);
      float: right;
    }
  }
</style>
